import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { usePopup } from '../../contexts/PopupContext';
import { getAvatarUrl } from '../../utils/avatarUtils';
import {
  sendFriendInvitation,
  acceptFriendInvitation,
  rejectFriendInvitation,
  cancelFriendInvitation,
  removeFriend,
  getMutualFriends
} from '../../utils/friendsService';
import { fetchUserActivityStats } from '../../utils/profileStatsService';
import {
  getCachedProfileView,
  setCachedProfileView,
} from '../../utils/profileViewCache';
import { useCreateNotification } from '../../hooks/useCreateNotification';
import Loading from '../layout/Loading';
import { ModalCloseIcon } from '../layout/Modal';
import { BadgeList, RoleBadge } from '../badges';
import {
  ProfileCardHourglassIcon,
  ProfileCardUserPlusIcon,
  ProfileCardXIcon,
  ProfileGenderFemaleIcon,
  ProfileGenderMaleIcon,
  ProfileFriendsRemoveTrashIcon,
  ProfileFriendsStatusCheckIcon,
  ProfilePlanSparklesIcon,
  ProfileSpotlightHeartIcon,
  ProfileStatCheckCircleIcon,
  ProfileStatGlobeIcon,
  ProfileStatUsersIcon,
} from '../profile/ProfileActivityIcons';
import { ProfileAvatarEditIcon, ProfileAvatarPlaceholderIcon } from '../profile/ProfileDashIcons';
import {
  formatProfileLastSeenPhrase,
  getProfileLastSeenLampTone,
} from '../../utils/formatProfileLastSeen';
import './ProfilePopup.css';

function isPostgrestNoRow(error) {
  if (!error) return false;
  if (error.code === 'PGRST116') return true;
  const blob = `${error.details || ''} ${error.message || ''}`;
  return /0 rows|contains 0 rows/i.test(blob);
}

function ProfilePopup({ userId, onClose }) {
  const { t, i18n } = useTranslation(['profile', 'shared']);
  const { openPopup } = usePopup();
  const createNotification = useCreateNotification({ cooldownMinutes: 5 }); // 5 minut cooldown
  const [profile, setProfile] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileUnavailable, setProfileUnavailable] = useState(false);
  const [staffInactiveModeration, setStaffInactiveModeration] = useState(false);
  const [staffDeactivateAction, setStaffDeactivateAction] = useState(null);
  const [friendStatus, setFriendStatus] = useState('none');
  const [friendStatusResolved, setFriendStatusResolved] = useState(false);
  const [friendInvitationId, setFriendInvitationId] = useState(null);
  const [processingFriendAction, setProcessingFriendAction] = useState(false);
  const [mutualFriends, setMutualFriends] = useState({ data: [], count: 0 });
  const [stats, setStats] = useState({
    participations: { upcoming: 0, completed: 0 },
    organized: 0,
    friendsCount: 0,
    favoriteCategory: null,
    uniqueCities: 0,
    // Dane dla systemu odznak
    completedEvents: 0,
    organizedEvents: 0,
    isBetaTester: false,
    preLaunchSubscriber: false,
    supporterDonor: false,
  });
  const friendsChannelRef = useRef(null);
  /** Odrzucamy odpowiedź tylko gdy użytkownik przełączył się na inny profil — nie przy zmianie `currentUser` dla tego samego `userId`. */
  const activeViewedUserIdRef = useRef(null);

  useEffect(() => {
    if (!userId) return;

    activeViewedUserIdRef.current = userId;
    let cancelled = false;

    const run = async () => {
      setProfileUnavailable(false);
      setStaffInactiveModeration(false);
      setStaffDeactivateAction(null);

      const { data: authData } = await supabase.auth.getUser();
      if (cancelled || userId !== activeViewedUserIdRef.current) return;

      const authUser = authData?.user ?? null;
      setCurrentUser(authUser);

      const viewerId = authUser?.id ?? null;
      let viewerRole = null;
      if (viewerId) {
        const { data: viewerRow } = await supabase
          .from('users')
          .select('user_role')
          .eq('id', viewerId)
          .maybeSingle();
        viewerRole = viewerRow?.user_role ?? null;
      }
      if (cancelled || userId !== activeViewedUserIdRef.current) return;

      const isSelf = Boolean(viewerId && viewerId === userId);
      const isStaff = viewerRole === 'admin' || viewerRole === 'moderator';

      const cached = getCachedProfileView(userId);
      const inactiveInCache = Boolean(cached?.profile && cached.profile.is_active === false);
      const skipCache = inactiveInCache && !isSelf && !isStaff;

      let usedWarmStart = false;
      if (cached && !skipCache) {
        setProfile(cached.profile);
        setStats(cached.stats);
        setLoading(false);
        usedWarmStart = true;
      } else {
        setLoading(true);
        setProfile(null);
      }

      const profileRes = await supabase.from('users').select('*').eq('id', userId).single();
      if (cancelled || userId !== activeViewedUserIdRef.current) return;

      const { data: userProfile, error } = profileRes;
      const noRow = isPostgrestNoRow(error);

      if (error || !userProfile) {
        if (noRow && !isSelf) {
          setProfile(null);
          setProfileUnavailable(true);
          setStaffInactiveModeration(false);
          setStaffDeactivateAction(null);
          setLoading(false);
          return;
        }
        console.error('Error loading profile:', error);
        if (!usedWarmStart) {
          setProfile(null);
          setLoading(false);
        }
        return;
      }

      const rowActive = userProfile.is_active !== false;

      if (!rowActive && isStaff && !isSelf) {
        const { data: action } = await supabase
          .from('account_actions')
          .select('reason, feedback, created_at')
          .eq('user_id', userId)
          .eq('action_type', 'deactivate')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (cancelled || userId !== activeViewedUserIdRef.current) return;
        setProfile(userProfile);
        setStaffInactiveModeration(true);
        setStaffDeactivateAction(action);
        setProfileUnavailable(false);
        setLoading(false);
        return;
      }

      if (!rowActive && !isStaff && !isSelf) {
        setProfile(null);
        setProfileUnavailable(true);
        setStaffInactiveModeration(false);
        setStaffDeactivateAction(null);
        setLoading(false);
        return;
      }

      setStaffInactiveModeration(false);
      setStaffDeactivateAction(null);
      setProfileUnavailable(false);
      setProfile(userProfile);
      setLoading(false);

      let stats;
      try {
        stats = await fetchUserActivityStats(userId);
      } catch (e) {
        console.error('Error loading stats:', e);
        return;
      }
      if (cancelled || userId !== activeViewedUserIdRef.current) return;
      setStats(stats);
      setCachedProfileView(userId, userProfile, stats);
    };

    run().catch((e) => {
      console.error('ProfilePopup load error:', e);
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
      activeViewedUserIdRef.current = null;
    };
  }, [userId]);

  const loadMutualFriends = useCallback(async () => {
    if (!currentUser?.id || !userId || currentUser.id === userId) {
      setMutualFriends({ data: [], count: 0 });
      return;
    }

    try {
      const result = await getMutualFriends(currentUser.id, userId);
      if (result.success) {
        setMutualFriends({ data: result.data || [], count: result.count || 0 });
      }
    } catch (error) {
      console.error('Error loading mutual friends:', error);
    }
  }, [currentUser?.id, userId]);

  const loadFriendStatus = useCallback(async () => {
    try {
      if (!currentUser || !userId) {
        setFriendStatus('none');
        setFriendInvitationId(null);
        setFriendStatusResolved(false);
        return;
      }

      if (currentUser.id === userId) {
        setFriendStatus('none');
        setFriendInvitationId(null);
        setFriendStatusResolved(true);
        return;
      }

      // Pobierz zaproszenie między użytkownikami
      const { data, error } = await supabase
        .from('friend_invitations')
        .select('*')
        .or(`and(sender_id.eq.${currentUser.id},receiver_id.eq.${userId}),and(sender_id.eq.${userId},receiver_id.eq.${currentUser.id})`)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error loading friend status:', error);
        setFriendStatus('none');
        setFriendStatusResolved(true);
        return;
      }

      if (!data) {
        setFriendStatus('none');
        setFriendInvitationId(null);
        setFriendStatusResolved(true);
        return;
      }

      setFriendInvitationId(data.id);

      // Określ status z perspektywy currentUser
      if (data.sender_id === currentUser.id && data.receiver_id === userId) {
        // Current user sent invitation
        if (data.status === 'pending') setFriendStatus('pending_sent');
        else if (data.status === 'accepted') setFriendStatus('accepted');
        else setFriendStatus('none');
      } else if (data.sender_id === userId && data.receiver_id === currentUser.id) {
        // Current user received invitation
        if (data.status === 'pending') setFriendStatus('pending_received');
        else if (data.status === 'accepted') setFriendStatus('accepted');
        else setFriendStatus('none');
      } else {
        setFriendStatus('none');
      }
      setFriendStatusResolved(true);
    } catch (error) {
      console.error('Error loading friend status:', error);
      setFriendStatus('none');
      setFriendStatusResolved(true);
    }
  }, [currentUser, userId]);

  const setupFriendsRealtimeSubscription = useCallback(() => {
    if (!currentUser || !userId || currentUser.id === userId) return;

    // Clean up existing subscription
    if (friendsChannelRef.current) {
      supabase.removeChannel(friendsChannelRef.current);
    }

    // Subscribe to changes in friend_invitations between these two users
    const channel = supabase
      .channel(`profile-friend-status-${currentUser.id}-${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'friend_invitations'
        },
        async (payload) => {
          // Sprawdź czy zmiana dotyczy relacji między currentUser a userId
          const invitation = payload.new || payload.old;
          if (
            invitation &&
            ((invitation.sender_id === currentUser.id && invitation.receiver_id === userId) ||
             (invitation.sender_id === userId && invitation.receiver_id === currentUser.id))
          ) {
            // Przeładuj status zaproszenia
            await loadFriendStatus();
          }
        }
      )
      .subscribe();

    friendsChannelRef.current = channel;
  }, [currentUser, userId, loadFriendStatus]);

  useEffect(() => {
    if (!userId || profileUnavailable || staffInactiveModeration) {
      if (friendsChannelRef.current) {
        supabase.removeChannel(friendsChannelRef.current);
        friendsChannelRef.current = null;
      }
      setMutualFriends({ data: [], count: 0 });
      setFriendStatus('none');
      setFriendInvitationId(null);
      setFriendStatusResolved(true);
      return;
    }

    setFriendStatusResolved(false);
    loadFriendStatus();
    loadMutualFriends();
    setupFriendsRealtimeSubscription();

    return () => {
      if (friendsChannelRef.current) {
        supabase.removeChannel(friendsChannelRef.current);
        friendsChannelRef.current = null;
      }
    };
  }, [
    userId,
    currentUser,
    profileUnavailable,
    staffInactiveModeration,
    loadFriendStatus,
    loadMutualFriends,
    setupFriendsRealtimeSubscription,
  ]);

  const handleEditProfile = () => {
    if (onClose) onClose();
    openPopup('edit-profile', {}, 0);
  };

  const handleSendInvitation = async () => {
    if (processingFriendAction || !currentUser) return;
    
    setProcessingFriendAction(true);
    try {
      const result = await sendFriendInvitation(userId);
      if (result.success) {
        await loadFriendStatus();
        
        // Utwórz powiadomienie dla odbiorcy zaproszenia
        // uniqueKey to ID nadawcy (currentUser.id), żeby nie można było spamować zaproszeniami
        // Pobierz dane nadawcy z profilu
        const { data: senderProfile } = await supabase
          .from('users')
          .select('name, nickname, first_name, last_name')
          .eq('id', currentUser.id)
          .single();
        
        const senderName = senderProfile?.nickname || 
          (senderProfile?.first_name && senderProfile?.last_name 
            ? `${senderProfile.first_name} ${senderProfile.last_name}` 
            : senderProfile?.name || 'Ktoś');
        
        const notificationResult = await createNotification({
          userId: userId, // Odbiorca zaproszenia
          type: 'friend_invitation',
          data: {
            sender_id: currentUser.id,
            sender_name: senderName
          },
          uniqueKey: currentUser.id // Anti-spam: ten sam użytkownik nie może wysłać wielu zaproszeń w krótkim czasie
        });

        if (!notificationResult.success && !notificationResult.skipped) {
          console.error('Error creating notification:', notificationResult.error);
          // Nie pokazujemy błędu użytkownikowi - zaproszenie zostało wysłane
        }
      } else {
        alert(t('profile.friends.errorSendInvitation') + ': ' + result.error);
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      alert(t('profile.friends.errorSendInvitation'));
    } finally {
      setProcessingFriendAction(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (processingFriendAction || !friendInvitationId) return;
    
    setProcessingFriendAction(true);
    try {
      const result = await acceptFriendInvitation(friendInvitationId);
      if (result.success) {
        await loadFriendStatus();
      } else {
        alert(t('profile.friends.errorAcceptInvitation') + ': ' + result.error);
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      alert(t('profile.friends.errorAcceptInvitation'));
    } finally {
      setProcessingFriendAction(false);
    }
  };

  const handleRejectInvitation = async () => {
    if (processingFriendAction || !friendInvitationId) return;
    
    setProcessingFriendAction(true);
    try {
      const result = await rejectFriendInvitation(friendInvitationId);
      if (result.success) {
        await loadFriendStatus();
      } else {
        alert(t('profile.friends.errorRejectInvitation') + ': ' + result.error);
      }
    } catch (error) {
      console.error('Error rejecting invitation:', error);
      alert(t('profile.friends.errorRejectInvitation'));
    } finally {
      setProcessingFriendAction(false);
    }
  };

  const handleCancelInvitation = async () => {
    if (processingFriendAction || !friendInvitationId) return;
    
    setProcessingFriendAction(true);
    try {
      const result = await cancelFriendInvitation(friendInvitationId);
      if (result.success) {
        await loadFriendStatus();
      } else {
        alert(t('profile.friends.errorCancelInvitation') + ': ' + result.error);
      }
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      alert(t('profile.friends.errorCancelInvitation'));
    } finally {
      setProcessingFriendAction(false);
    }
  };

  const handleRemoveFriend = async () => {
    if (processingFriendAction) return;
    
    if (!window.confirm(t('profile.friends.confirmRemoveFriend'))) {
      return;
    }

    setProcessingFriendAction(true);
    try {
      const result = await removeFriend(userId);
      if (result.success) {
        await loadFriendStatus();
      } else {
        alert(t('profile.friends.errorRemoveFriend') + ': ' + result.error);
      }
    } catch (error) {
      console.error('Error removing friend:', error);
      alert(t('profile.friends.errorRemoveFriend'));
    } finally {
      setProcessingFriendAction(false);
    }
  };

  const profileModalHeader = (
    <div className="modal-header">
      <div className="modal-header-leading modal-header-leading--solo">
        <h2>{t('profile.popup.title')}</h2>
      </div>
      {onClose ? (
        <button
          type="button"
          className="modal-close"
          onClick={onClose}
          aria-label={t('profile.popup.close')}
        >
          <ModalCloseIcon />
        </button>
      ) : null}
    </div>
  );

  if (loading) {
    return (
      <div className="profile-popup-content">
        {profileModalHeader}
        <Loading className="profile-popup-loading" size="medium" />
      </div>
    );
  }

  if (profileUnavailable) {
    return (
      <div className="profile-popup-content">
        {profileModalHeader}
        <div className="profile-popup-error profile-popup-message--inactive">
          <h3 className="profile-popup-inactive-title">{t('profile.popup.inactiveUnavailableTitle')}</h3>
          <p>{t('profile.popup.inactiveUnavailableBody')}</p>
        </div>
      </div>
    );
  }

  if (staffInactiveModeration) {
    const deactivatedAt = profile?.deactivated_at;
    const loc = i18n.language === 'pl' ? 'pl-PL' : 'en-US';
    const deactivatedLine = deactivatedAt
      ? t('profile.popup.staffInactiveDeactivatedAt', {
          date: new Date(deactivatedAt).toLocaleString(loc, { dateStyle: 'medium', timeStyle: 'short' }),
        })
      : t('profile.popup.staffInactiveDeactivatedAtUnknown');

    const rawReason = staffDeactivateAction?.reason;
    const reasonLabel = rawReason
      ? t(`profile.settings.pages.account.deactivateForm.reasons.${rawReason}`, { defaultValue: rawReason })
      : '—';

    const feedbackText = (staffDeactivateAction?.feedback || '').trim();
    const feedbackDisplay = feedbackText || t('profile.popup.staffInactiveNoFeedback');

    const noRecord = !staffDeactivateAction;

    return (
      <div className="profile-popup-content">
        {profileModalHeader}
        <div className="profile-popup-staff-moderation">
          <h3 className="profile-popup-staff-title">{t('profile.popup.staffInactiveTitle')}</h3>
          <p className="profile-popup-staff-sub">{t('profile.popup.staffInactiveSubtitle')}</p>
          <p className="profile-popup-staff-date">{deactivatedLine}</p>
          {noRecord ? (
            <p className="profile-popup-staff-hint">{t('profile.popup.staffInactiveNoDeactivateRecord')}</p>
          ) : (
            <>
              <div className="profile-popup-staff-block">
                <div className="profile-popup-staff-label">{t('profile.popup.staffInactiveReasonLabel')}</div>
                <div className="profile-popup-staff-value">{reasonLabel}</div>
              </div>
              <div className="profile-popup-staff-block">
                <div className="profile-popup-staff-label">{t('profile.popup.staffInactiveFeedbackLabel')}</div>
                <div className="profile-popup-staff-value">{feedbackDisplay}</div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-popup-content">
        {profileModalHeader}
        <div className="profile-popup-error">
          <p>{t('shared.common.error')}</p>
        </div>
      </div>
    );
  }

  const locale = i18n.language === 'pl' ? 'pl-PL' : 'en-US';
  const isOwnProfile = currentUser && currentUser.id === userId;
  const nameNotSet = t('profile.info.nameNotSet');
  const nick = (profile.nickname || '').trim();
  const firstLast =
    profile.first_name && profile.last_name
      ? `${String(profile.first_name).trim()} ${String(profile.last_name).trim()}`.trim()
      : '';
  const legacyName = (profile.name || '').trim();
  const legalOrDisplay = firstLast || legacyName;
  /** Krótka etykieta (np. wspólni znajomi) — jak wcześniej: nick w pierwszej kolejności. */
  const displayName = nick || legalOrDisplay || nameNotSet;
  const showNickInParens =
    Boolean(nick && legalOrDisplay && nick.toLowerCase() !== legalOrDisplay.toLowerCase());
  const namePrimary = showNickInParens ? legalOrDisplay : displayName;

  // Oblicz wiek
  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const age = profile.birth_date ? calculateAge(profile.birth_date) : null;
  const normalizedGender = profile.gender === 'male'
    ? 'M'
    : profile.gender === 'female'
      ? 'F'
      : profile.gender;
  const genderIcon = normalizedGender === 'F'
    ? <ProfileGenderFemaleIcon size={21} />
    : normalizedGender === 'M'
      ? <ProfileGenderMaleIcon size={21} />
      : null;

  const showLastSeen = Boolean(profile.last_seen_at && (isOwnProfile || !profile.hide_last_activity));
  const lastSeenLine = showLastSeen
    ? formatProfileLastSeenPhrase(profile.last_seen_at, t, i18n)
    : null;
  const lastSeenLampTone = showLastSeen
    ? getProfileLastSeenLampTone(profile.last_seen_at)
    : 'offline';

  // Formatuj datę dołączenia
  const formatJoinDate = (date) => {
    if (!date) return '';
    const joinDate = new Date(date);
    const month = joinDate.toLocaleDateString(locale, { month: 'long' });
    const year = joinDate.getFullYear();
    return `${month} ${year}`;
  };

  return (
    <div className="profile-popup-content">
      {profileModalHeader}

      {/* Hero: avatar, identity, decorative layer */}
      <div className="profile-popup-hero">
        <div className="profile-popup-hero-decoration" aria-hidden />
        {genderIcon && (
          <div
            className="profile-popup-gender-badge"
            title={t(`profile.info.genderOptions.${normalizedGender}`)}
            aria-label={`${t('profile.info.gender')}: ${t(`profile.info.genderOptions.${normalizedGender}`)}`}
          >
            {genderIcon}
          </div>
        )}
        <div className="profile-popup-hero-grid">
          <div className="profile-popup-avatar-section">
            <div className="profile-popup-avatar-ring">
              <div className="profile-popup-avatar-container">
                {profile.avatar_url ? (
                  <img
                    src={getAvatarUrl(profile.avatar_url, 200)}
                    alt={t('profile.info.avatarAlt')}
                    className="profile-popup-avatar"
                  />
                ) : (
                  <div className="profile-popup-avatar-placeholder">
                    <ProfileAvatarPlaceholderIcon size={40} className="profile-popup-avatar-placeholder-svg" />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="profile-popup-name-section">
            <h2 className="profile-popup-name">
              <span className="profile-popup-name-primary">{namePrimary}</span>
              {showNickInParens && (
                <span className="profile-popup-name-nick"> ({nick})</span>
              )}
              {age != null && age > 0 && (
                <span className="profile-popup-name-age">({age} {t('profile.info.yearsOld')})</span>
              )}
              {isOwnProfile && (
                <span className="profile-popup-badge">
                  <ProfilePlanSparklesIcon size={14} className="profile-popup-badge-icon" aria-hidden />
                  {t('profile.popup.itsYou')}
                </span>
              )}
            </h2>
            <div className="profile-popup-role-row">
              <RoleBadge userRole={profile.user_role} size="small" />
            </div>
          </div>
        </div>
        {lastSeenLine && (
          <div
            className={`profile-popup-last-seen is-${lastSeenLampTone}`}
            role="status"
            aria-label={`${t('profile.popup.lastSeenLabel')}: ${lastSeenLine}`}
          >
            <span className="profile-popup-last-seen-label">
              {t('profile.popup.lastSeenLabel')}
            </span>
            <span className="profile-popup-last-seen-value-row">
              <span className="profile-popup-last-seen-lamp" aria-hidden="true" />
              <span className="profile-popup-last-seen-value">{lastSeenLine}</span>
            </span>
          </div>
        )}
      </div>

      {profile.description && (
        <div className="profile-popup-about">
          <p className="profile-popup-about-text">"{profile.description}"</p>
        </div>
      )}

      <div className="profile-popup-actions">
      {isOwnProfile ? (
        <div className="profile-popup-edit-section">
          <button
            className="profile-popup-edit-button"
            onClick={handleEditProfile}
          >
            <span className="profile-popup-edit-button-inner">
              <ProfileAvatarEditIcon size={18} className="profile-popup-edit-button-icon" aria-hidden />
              {t('profile.popup.editProfile')}
            </span>
          </button>
        </div>
      ) : (
        <div className="profile-popup-friend-section">
          <div className="profile-popup-friend-section-header">
            <h3 className="profile-popup-friend-section-title">
              {t('profile.popup.friendshipSectionTitle', 'Relacja')}
            </h3>
          </div>
          <div
            className="profile-popup-friend-section-body"
            aria-busy={!friendStatusResolved}
          >
            {!friendStatusResolved ? (
              <div className="profile-popup-friend-loading-state">
                <span className="profile-popup-friend-loading-line" aria-hidden />
                <span>{t('profile.popup.friendshipLoading', 'Ładowanie relacji...')}</span>
              </div>
            ) : null}
            {friendStatusResolved && friendStatus === 'none' && (
              <button
                className="profile-popup-friend-button profile-popup-friend-button-primary"
                onClick={handleSendInvitation}
                disabled={processingFriendAction}
              >
                {processingFriendAction ? (
                  '...'
                ) : (
                  <span className="profile-popup-friend-button-inner">
                    <ProfileCardUserPlusIcon size={17} aria-hidden />
                    {t('profile.popup.sendInvitation')}
                  </span>
                )}
              </button>
            )}
            {friendStatusResolved && friendStatus === 'pending_sent' && (
              <button
                className="profile-popup-friend-button profile-popup-friend-button-secondary"
                onClick={handleCancelInvitation}
                disabled={processingFriendAction}
              >
                {processingFriendAction ? (
                  '...'
                ) : (
                  <span className="profile-popup-friend-button-inner">
                    <ProfileCardHourglassIcon size={17} aria-hidden />
                    {t('profile.popup.invitationPending')}
                  </span>
                )}
              </button>
            )}
            {friendStatusResolved && friendStatus === 'pending_received' && (
              <div className="profile-popup-friend-buttons-group">
                <button
                  className="profile-popup-friend-button profile-popup-friend-button-accept"
                  onClick={handleAcceptInvitation}
                  disabled={processingFriendAction}
                >
                  {processingFriendAction ? (
                    '...'
                  ) : (
                    <span className="profile-popup-friend-button-inner">
                      <ProfileFriendsStatusCheckIcon size={17} aria-hidden />
                      {t('profile.popup.acceptInvitation')}
                    </span>
                  )}
                </button>
                <button
                  className="profile-popup-friend-button profile-popup-friend-button-reject"
                  onClick={handleRejectInvitation}
                  disabled={processingFriendAction}
                >
                  {processingFriendAction ? (
                    '...'
                  ) : (
                    <span className="profile-popup-friend-button-inner">
                      <ProfileCardXIcon size={17} aria-hidden />
                      {t('profile.friends.reject')}
                    </span>
                  )}
                </button>
              </div>
            )}
            {friendStatusResolved && friendStatus === 'accepted' && (
              <div className="profile-popup-friend-buttons-group">
                <span className="profile-popup-friend-status">
                  <ProfileFriendsStatusCheckIcon size={17} aria-hidden />
                  {t('profile.popup.alreadyFriends')}
                </span>
                <button
                  type="button"
                  className="profile-popup-friend-button profile-popup-friend-button-remove"
                  onClick={handleRemoveFriend}
                  disabled={processingFriendAction}
                  title={t('profile.popup.removeFriendAria')}
                  aria-label={t('profile.popup.removeFriendAria')}
                >
                  {processingFriendAction ? (
                    '...'
                  ) : (
                    <span className="profile-popup-friend-button-inner">
                      <ProfileFriendsRemoveTrashIcon size={17} aria-hidden />
                      {t('profile.popup.removeFriend')}
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      </div>

      {!isOwnProfile && mutualFriends.count > 0 && (
        <div className="profile-popup-mutual-friends">
          <span className="profile-popup-mutual-friends-icon" aria-hidden>
            <ProfileStatUsersIcon size={20} className="profile-popup-mutual-friends-icon-svg" />
          </span>
          <span className="profile-popup-mutual-friends-text">
            {t('profile.popup.mutualFriends', {
              name: displayName,
              count: mutualFriends.count,
              defaultValue: `Ty i ${displayName} macie ${mutualFriends.count} wspólnych znajomych`
            })}
          </span>
        </div>
      )}

      <div className="profile-popup-sections">
      <div className="profile-popup-section profile-popup-section--badges">
        <div className="profile-popup-section-header">
          <div className="profile-popup-section-bar"></div>
          <h3 className="profile-popup-section-title">
            {t('profile.popup.badges', 'Odznaki')}
          </h3>
        </div>
        <BadgeList 
          userRole={profile.user_role}
          stats={stats}
          showRoleBadge={true}
          showActivityBadges={true}
          size="medium"
        />
      </div>

      {(stats.favoriteCategory ||
        stats.uniqueCities > 0 ||
        stats.participations.completed > 0) && (
        <div className="profile-popup-section profile-popup-section--activity">
          <div className="profile-popup-section-header">
            <div className="profile-popup-section-bar"></div>
            <h3 className="profile-popup-section-title">
              {t('profile.popup.highlights', 'Aktywność')}
            </h3>
          </div>
          <div className="profile-popup-highlights">
            {stats.favoriteCategory && (
              <div className="profile-popup-highlight">
                <span className="profile-popup-highlight-icon" aria-hidden>
                  <ProfileSpotlightHeartIcon size={25} className="profile-popup-highlight-icon-svg" />
                </span>
                <span className="profile-popup-highlight-text">
                  {t('profile.popup.favoriteCategory', 'Ulubiona kategoria')}: <strong>{t(`events:categories.${stats.favoriteCategory}`, stats.favoriteCategory)}</strong>
                </span>
              </div>
            )}
            {stats.uniqueCities > 0 && (
              <div className="profile-popup-highlight">
                <span className="profile-popup-highlight-icon" aria-hidden>
                  <ProfileStatGlobeIcon size={25} className="profile-popup-highlight-icon-svg" />
                </span>
                <span className="profile-popup-highlight-text">
                  {t('profile.popup.uniqueLocations', 'Unikalne lokalizacje')}: <strong>{stats.uniqueCities}</strong>
                </span>
              </div>
            )}
            {stats.participations.completed > 0 && (
              <div className="profile-popup-highlight">
                <span className="profile-popup-highlight-icon" aria-hidden>
                  <ProfileStatCheckCircleIcon size={25} className="profile-popup-highlight-icon-svg" />
                </span>
                <span className="profile-popup-highlight-text">
                  {t('profile.popup.completedEvents', 'Ukończonych wydarzeń')}: <strong>{stats.participations.completed}</strong>
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="profile-popup-section profile-popup-section--stats">
        <div className="profile-popup-section-header">
          <div className="profile-popup-section-bar"></div>
          <h3 className="profile-popup-section-title">
            {t('profile.popup.statistics')}
          </h3>
        </div>
        <div className="profile-popup-stats-cards">
          <div className="profile-popup-stat-card">
            <div className="profile-popup-stat-value participations">
              {stats.participations.upcoming}
            </div>
            <div className="profile-popup-stat-label">
              {t('profile.popup.upcomingEvents', 'Nadchodzące')}
            </div>
          </div>
          <div className="profile-popup-stat-card">
            <div className="profile-popup-stat-value organized">
              {stats.organized}
            </div>
            <div className="profile-popup-stat-label">
              {t('profile.popup.organized')}
            </div>
          </div>
          <div className="profile-popup-stat-card">
            <div className="profile-popup-stat-value friends">
              {stats.friendsCount}
            </div>
            <div className="profile-popup-stat-label">
              {t('profile.friends.friendsCount')}
            </div>
          </div>
        </div>
      </div>
      </div>

      {profile.created_at && (
        <div className="profile-popup-footer">
          <p className="profile-popup-member-since">
            {t('profile.popup.memberSince')} {formatJoinDate(profile.created_at)}
          </p>
        </div>
      )}
    </div>
  );
}

export default ProfilePopup;
